// Global Instructions Rule Applied!
// Shape returned by view__fund_partner_allocation, the Partner allocation
// report of the fund Statements tab (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md
// 4.4, PR 3): for a financial year (to an as-of date inside an open year)
// one row per partner x K-1 tax category - the fund total, the pro-rata
// share, the curative part, the allocation (booked for a closed year,
// unbooked for the open year), the carry-forward, the partner's own stamped
// lines and the management fees among them - with totals per category tied
// to the ledger. Mirrors backend/src/data_manager/data_models/
// fund_partner_allocation_record.py. Ids are plain strings (Dataverse GUIDs
// fail zod's .uuid()).
import { z } from 'zod'

export const FundPartnerAllocationRowSchema = z
  .object({
    investors__id: z.string(),
    investors__name: z.string(),
    tax_categories__id: z.number().int().nullish(),
    tax_categories__code: z.string(),
    tax_categories__label: z.string(),
    tax_categories__k1_box: z.string().nullish(),
    prog__cat_key: z.string(),
    prog__share: z.number(),
    prog__fund_total: z.number(),
    prog__fund_total_fmt: z.string().nullish(),
    prog__pro_rata: z.number(),
    prog__pro_rata_fmt: z.string().nullish(),
    prog__curative: z.number(),
    prog__curative_fmt: z.string().nullish(),
    prog__allocated: z.number(),
    prog__allocated_fmt: z.string().nullish(),
    prog__override: z.number().nullish(),
    prog__carry_forward: z.number(),
    prog__carry_forward_fmt: z.string().nullish(),
    prog__direct: z.number(),
    prog__direct_fmt: z.string().nullish(),
    prog__management_fees: z.number(),
    prog__management_fees_fmt: z.string().nullish(),
    prog__partner_total: z.number(),
    prog__partner_total_fmt: z.string().nullish()
  })
  .strict()

export type FundPartnerAllocationRow = z.infer<typeof FundPartnerAllocationRowSchema>

export const FundPartnerAllocationCategorySchema = z
  .object({
    tax_categories__id: z.number().int().nullish(),
    tax_categories__code: z.string(),
    tax_categories__label: z.string(),
    tax_categories__k1_box: z.string().nullish(),
    prog__cat_key: z.string(),
    prog__fund_total: z.number(),
    prog__fund_total_fmt: z.string().nullish(),
    prog__pro_rata: z.number(),
    prog__curative: z.number(),
    prog__allocated: z.number(),
    prog__allocated_fmt: z.string().nullish(),
    prog__carry_forward: z.number(),
    prog__direct: z.number(),
    prog__direct_fmt: z.string().nullish(),
    prog__management_fees: z.number(),
    prog__ledger_total: z.number(),
    prog__ledger_total_fmt: z.string().nullish(),
    prog__difference: z.number(),
    prog__balanced: z.boolean(),
    prog__tied: z.boolean()
  })
  .strict()

export type FundPartnerAllocationCategory = z.infer<typeof FundPartnerAllocationCategorySchema>

export const FundPartnerAllocationTotalsSchema = z
  .object({
    prog__fund_total: z.number(),
    prog__allocated: z.number(),
    prog__direct: z.number(),
    prog__management_fees: z.number(),
    prog__curative: z.number(),
    prog__carry_forward: z.number(),
    prog__ledger_total: z.number(),
    prog__difference: z.number(),
    prog__tied: z.boolean()
  })
  .strict()

export type FundPartnerAllocationTotals = z.infer<typeof FundPartnerAllocationTotalsSchema>

export const FundPartnerAllocationSchema = z
  .object({
    fund: z
      .object({
        funds__id: z.string(),
        funds__name: z.string().nullish(),
        currencies__symbol: z.string().nullish()
      })
      .strict(),
    fin_year: z
      .object({
        id: z.string(),
        fin_year: z.number().int(),
        year_start_date: z.string().nullish(),
        year_end_date: z.string().nullish(),
        closed: z.boolean().nullish(),
        window_end: z.string().nullish()
      })
      .strict(),
    per_fin_year: z.boolean(),
    basis_label: z.string(),
    is_booked: z.boolean(),
    provisional: z.boolean(),
    rows: z.array(FundPartnerAllocationRowSchema),
    categories: z.array(FundPartnerAllocationCategorySchema),
    totals: FundPartnerAllocationTotalsSchema,
    status_message: z.string().nullish()
  })
  .strict()

export type FundPartnerAllocation = z.infer<typeof FundPartnerAllocationSchema>

export const validateFundPartnerAllocation = (data: unknown): FundPartnerAllocation => {
  const result = FundPartnerAllocationSchema.safeParse(data)
  if (!result.success) {
    throw new Error(`Fund partner allocation validation failed: ${result.error.message}`)
  }
  return result.data
}
